import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Sparkles, Upload, Image as ImageIcon, X, Loader2, CheckCircle2, AlertCircle, RotateCcw, Lock, Plus, FileBox } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { createCase, updateCase, getCase, getCasePrivateOwner, setCasePrivate, generateCaseId } from "../services/cases";
import { ensureAccountCode } from "../services/profiles";
import { uploadMany, uploadFileResumable } from "../services/storage";
import { notifyNewCase } from "../services/emails";
import { getCaseCadFiles } from "../lib/cad";
import {
  emptyRestoration, isImplantType, parseLegacyTeeth, teethLabelString, teethList,
} from "../lib/dental";
import { Progress } from "../components/ui/progress";
import { analyzeCase, budgetAdvisor } from "../services/gemini";
import { AiAnalysisPanel } from "../components/AiAnalysisPanel";
import { RestorationForm } from "../components/RestorationForm";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Skeleton } from "../components/ui/skeleton";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";

// Persisted shape for one restoration (drops local-only keys).
const serializeRestoration = (r, cadFiles) => ({
  type: r.type,
  material: r.material,
  shade: r.shade,
  clinicalInstructions: r.clinicalInstructions,
  teeth: r.teeth || {},
  cadFiles: cadFiles || [],
  implant: isImplantType(r.type) ? r.implant : null,
});

export default function CreateCase() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { id: editId } = useParams();
  const { user, profile } = useAuth();

  const [form, setForm] = useState({
    title: "", deadline: "", budget: "", toothNumbering: "fdi",
    patientName: "", patientId: "", doctorName: "", clinicCaseId: "", modelEngraving: "",
    includeModels: false, includeGingiva: false,
    sharePatientName: false, shareDoctorName: false, shareClinicCaseId: false,
  });
  const [restorations, setRestorations] = useState([emptyRestoration()]);
  const [caseNumber, setCaseNumber] = useState("");
  const [photos, setPhotos] = useState([]);
  const [existingPhotos, setExistingPhotos] = useState([]);
  const [ai, setAi] = useState(null);
  const [aiState, setAiState] = useState({ loading: false, error: false, errorType: null });
  const [budgetAi, setBudgetAi] = useState({ loading: false, data: null });
  const [saving, setSaving] = useState(false);
  const [stlUp, setStlUp] = useState({ status: "idle", pct: 0 });
  const [retryCtx, setRetryCtx] = useState(null);
  const [createdId, setCreatedId] = useState(null);
  const [loadingCase, setLoadingCase] = useState(!!editId);
  const [confirmPublishOpen, setConfirmPublishOpen] = useState(false);

  const numbering = form.toothNumbering;

  // New cases default to the lab's saved numbering preference (still per-case editable).
  const prefApplied = useRef(false);
  useEffect(() => {
    if (!editId && !prefApplied.current && profile?.numberingPreference) {
      prefApplied.current = true;
      setForm((f) => ({ ...f, toothNumbering: profile.numberingPreference }));
    }
  }, [editId, profile?.numberingPreference]);

  const aiWarnings = ai?.warnings ? (ai.warnings.en || ai.warnings.es || []) : [];
  const hasAiWarnings = Array.isArray(aiWarnings) && aiWarnings.length > 0;

  const newCadCount = restorations.reduce((n, r) => n + r.cadFiles.length, 0);
  const existingCadCount = restorations.reduce((n, r) => n + r.existingCad.length, 0);
  const isImplantCase = restorations.some((r) => isImplantType(r.type));
  useEffect(() => {
    if (!isImplantCase && form.includeGingiva) set("includeGingiva", false);
  }, [isImplantCase, form.includeGingiva]);
  const totalCadCount = newCadCount + existingCadCount;

  useEffect(() => {
    if (!editId) return;
    (async () => {
      try {
        const c = await getCase(editId);
        if (c) {
          const priv = await getCasePrivateOwner(editId);
          setForm({
            title: c.title || "", deadline: c.deadline || "", budget: c.budget || "",
            toothNumbering: c.toothNumbering || "fdi",
            patientName: priv?.patientName || "", patientId: priv?.patientId || "",
            doctorName: priv?.doctorName || "", clinicCaseId: priv?.clinicCaseId || "",
            modelEngraving: priv?.modelEngraving || "",
            sharePatientName: !!priv?.sharePatientName, shareDoctorName: !!priv?.shareDoctorName,
            shareClinicCaseId: !!priv?.shareClinicCaseId,
          });
          setCaseNumber(c.caseNumber || "");
          setExistingPhotos(Array.isArray(c.photos) ? c.photos : []);
          setAi(c.ai || null);
          if (c.aiRecommended) setBudgetAi({ loading: false, data: c.aiRecommended });
          if (Array.isArray(c.restorations) && c.restorations.length) {
            setRestorations(c.restorations.map((r) => ({
              ...emptyRestoration(),
              type: r.type || "crown", material: r.material || "zirconia",
              shade: r.shade || "", clinicalInstructions: r.clinicalInstructions || "",
              teeth: r.teeth || {},
              existingCad: Array.isArray(r.cadFiles) ? r.cadFiles : [],
              cadFiles: [],
              implant: r.implant || emptyRestoration().implant,
            })));
          } else {
            // Legacy case: migrate the flat fields into a single restoration.
            setRestorations([{
              ...emptyRestoration(),
              type: c.restorationType || "crown", material: c.material || "zirconia",
              shade: c.shade || "", clinicalInstructions: c.clinicalInstructions || "",
              teeth: parseLegacyTeeth(c.toothNumbers),
              existingCad: getCaseCadFiles(c),
            }]);
          }
        }
      } catch {
        toast.error(t("case.loadError"));
      } finally {
        setLoadingCase(false);
      }
    })();
  }, [editId]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const updateRestoration = (idx, next) => setRestorations((rs) => rs.map((r, i) => (i === idx ? next : r)));
  const addRestoration = () => setRestorations((rs) => [...rs, emptyRestoration()]);
  const removeRestoration = (idx) => setRestorations((rs) => rs.filter((_, i) => i !== idx));

  const addPhotos = (fileList) => {
    const incoming = Array.from(fileList || []);
    if (incoming.length) setPhotos((prev) => [...prev, ...incoming]);
  };
  const removePhoto = (idx) => setPhotos((prev) => prev.filter((_, i) => i !== idx));
  const removeExistingPhoto = (idx) => setExistingPhotos((prev) => prev.filter((_, i) => i !== idx));

  const runAnalysis = async () => {
    if (!form.title.trim()) {
      toast.error("Add a case title first");
      return;
    }
    setAiState({ loading: true, error: false, errorType: null });
    setAi(null);
    try {
      const res = await analyzeCase({
        title: form.title,
        deadline: form.deadline,
        budget: form.budget,
        tooth_numbering: numbering,
        restorations: restorations.map((r) => ({
          type: r.type, material: r.material, shade: r.shade,
          clinicalInstructions: r.clinicalInstructions, teeth: r.teeth,
          implant: isImplantType(r.type) ? r.implant : null,
        })),
        has_photos: photos.length > 0 || existingPhotos.length > 0,
        has_stl: totalCadCount > 0,
        include_models: form.includeModels,
        include_gingiva: form.includeGingiva,
        model_name_requested: form.includeModels ? form.modelEngraving : "",
      });
      if (res.status === "ok") {
        setAi(res.analysis);
        setAiState({ loading: false, error: false, errorType: null });
        toast.success(t("case.aiDone"));
      } else {
        setAiState({ loading: false, error: true, errorType: res.error_type });
        toast.warning(res.error_type === "quota" ? t("ai.quotaError") : t("ai.genericError"));
      }
    } catch (e) {
      setAiState({ loading: false, error: true, errorType: "api" });
      toast.error(t("ai.genericError"));
    }
  };

  const runBudgetAdvisor = async () => {
    if (!form.title.trim()) {
      toast.error(t("case.titleRequired"));
      return;
    }
    setBudgetAi({ loading: true, data: null });
    try {
      const res = await budgetAdvisor({
        tooth_numbering: numbering,
        deadline: form.deadline,
        clinical_instructions: restorations.map((r) => r.clinicalInstructions).filter(Boolean).join(" | "),
        has_photos: photos.length > 0 || existingPhotos.length > 0,
        has_cad: totalCadCount > 0,
        restorations: restorations.map((r) => ({
          type: r.type, material: r.material, shade: r.shade,
          clinicalInstructions: r.clinicalInstructions, teeth: r.teeth,
          implant: isImplantType(r.type) ? r.implant : null,
        })),
        include_models: form.includeModels,
        include_gingiva: form.includeGingiva,
        locale: i18n.language?.split("-")[0] || "en",
      });
      if (res.status === "ok") {
        setBudgetAi({ loading: false, data: { recommended: res.recommended, confidence: res.confidence, reason: res.reason } });
        toast.success(t("budget.done"));
      } else {
        setBudgetAi({ loading: false, data: null });
        toast.warning(res.error_type === "quota" ? t("ai.quotaError") : t("ai.genericError"));
      }
    } catch (e) {
      setBudgetAi({ loading: false, data: null });
      toast.error(t("ai.genericError"));
    }
  };

  // Derive legacy/flat case fields (for Marketplace filters, CaseCard, backward compat).
  const derivedCaseFields = () => ({
    restorationType: restorations[0]?.type || "other",
    material: restorations[0]?.material || "other",
    shade: restorations[0]?.shade || "",
    toothNumbers: restorations.map((r) => teethLabelString(r.teeth, numbering)).filter(Boolean).join(" | "),
    clinicalInstructions: restorations.map((r) => r.clinicalInstructions).filter(Boolean).join("\n\n"),
  });

  // Upload every restoration's new CAD files, then persist the restorations array
  // plus an aggregated case-level cadFiles list (used by the 3D viewer).
  const uploadCadAndFinalize = async (id, status) => {
    setStlUp({ status: "uploading", pct: 0 });
    try {
      const total = Math.max(newCadCount, 1);
      let done = 0;
      const finalRestorations = [];
      for (const r of restorations) {
        const uploaded = [...r.existingCad];
        for (const file of r.cadFiles) {
          const u = await uploadFileResumable(file, `cases/${id}/cad`, (pct) => {
            const overall = Math.round(((done + pct / 100) / total) * 100);
            setStlUp({ status: overall >= 100 ? "processing" : "uploading", pct: overall });
          });
          uploaded.push({ url: u.url, name: u.name });
          done += 1;
        }
        finalRestorations.push(serializeRestoration(r, uploaded));
      }
      const aggregated = finalRestorations.flatMap((r) => r.cadFiles);
      setStlUp({ status: "done", pct: 100 });
      await updateCase(id, {
        restorations: finalRestorations,
        cadFiles: aggregated,
        stlUrl: aggregated[0]?.url || "",
        stlName: aggregated[0]?.name || "",
      });
      toast.success(status === "Published" ? t("case.published") : t(editId ? "case.updated" : "case.created"));
      if (status === "Published") notifyNewCase(id, window.location.origin);
      navigate(`/cases/${id}`);
    } catch (e) {
      setStlUp({ status: "error", pct: 0 });
      setRetryCtx({ id, status });
      setSaving(false);
      toast.error(t("upload.error"));
    }
  };

  const retryStl = async () => {
    if (!retryCtx) return;
    setSaving(true);
    await uploadCadAndFinalize(retryCtx.id, retryCtx.status);
  };

  const handlePublishClick = () => {
    if (totalCadCount === 0) {
      toast.error(t("cad.atLeastOne"));
      return;
    }
    if (restorations.some((r) => teethList(r.teeth).length === 0)) {
      toast.error(t("restoration.atLeastOneTeeth"));
      return;
    }
    if (hasAiWarnings) {
      setConfirmPublishOpen(true);
      return;
    }
    save("Published");
  };

  const save = async (status, opts = {}) => {
    if (!form.title.trim()) {
      toast.error(t("case.titleRequired"));
      return;
    }
    if (status === "Published" && totalCadCount === 0) {
      toast.error(t("cad.atLeastOne"));
      return;
    }
    const ackFields = opts.acknowledgeWarnings
      ? { aiWarningsAcknowledged: true, aiWarningsAcknowledgedAt: Date.now(), acknowledgedWarnings: aiWarnings }
      : {};
    const {
      patientName, patientId, doctorName, clinicCaseId, modelEngraving,
      sharePatientName, shareDoctorName, shareClinicCaseId, ...technical
    } = form;
    const ownerData = {
      patientName, patientId, doctorName, clinicCaseId, modelEngraving,
      sharePatientName, shareDoctorName, shareClinicCaseId,
    };
    const sharedData = {
      modelEngraving: form.includeModels ? modelEngraving : "",
      ...(sharePatientName ? { patientName } : {}),
      ...(shareDoctorName ? { doctorName } : {}),
      ...(shareClinicCaseId ? { clinicCaseId } : {}),
    };
    // Restorations with only their already-uploaded CAD (new files uploaded during finalize).
    const restorationsSoFar = restorations.map((r) => serializeRestoration(r, r.existingCad));
    setSaving(true);
    try {
      let id = editId;
      let number = caseNumber;
      const commonData = {
        ...technical,
        ...derivedCaseFields(),
        offeredPrice: Number(form.budget) || 0,
        aiRecommended: budgetAi.data || null,
        restorations: restorationsSoFar,
        status,
        ai: ai || null,
        ...ackFields,
      };
      if (editId) {
        await updateCase(editId, { ...commonData, photos: existingPhotos });
      } else {
        const accountCode = profile?.accountCode || (await ensureAccountCode(user.uid, profile?.name || user.email));
        number = await generateCaseId(accountCode, user.uid);
        id = await createCase({
          ...commonData,
          caseNumber: number,
          ownerId: user.uid,
          ownerName: profile?.name || user.email,
          // Flag cases created by internal demo/test accounts so they never leak
          // into the public Marketplace (owner-based demo detection at creation).
          isDemo: /@demo\.com$/i.test(user.email || ""),
          photos: [],
          stlUrl: "",
          stlName: "",
        });
        setCreatedId(id);
      }
      try {
        await setCasePrivate(id, ownerData, sharedData);
      } catch {
        toast.warning(t("case.privateSaveFailed"));
      }
      try {
        if (photos.length) {
          const up = await uploadMany(photos, `cases/${id}/photos`);
          await updateCase(id, { photos: [...existingPhotos, ...up.map((u) => u.url)] });
        }
      } catch {
        toast.warning(t("case.photoUploadFailed"));
      }
      if (newCadCount > 0) {
        await uploadCadAndFinalize(id, status);
        return;
      }
      // No new CAD: aggregate the existing CAD for the viewer.
      const aggregated = restorationsSoFar.flatMap((r) => r.cadFiles);
      await updateCase(id, {
        cadFiles: aggregated,
        stlUrl: aggregated[0]?.url || "",
        stlName: aggregated[0]?.name || "",
      });
      toast.success(status === "Published" ? t("case.published") : t(editId ? "case.updated" : "case.created"));
      if (status === "Published") notifyNewCase(id, window.location.origin);
      navigate(`/cases/${id}`);
    } catch (e) {
      toast.error(e?.message || t("case.saveFailed"));
      setSaving(false);
    }
  };

  if (loadingCase) {
    return (
      <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:px-8">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="mt-8 h-96 w-full rounded-lg" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:px-8">
      <h1 className="font-heading text-3xl font-bold tracking-tight">{editId ? t("case.editTitle") : t("case.createTitle")}</h1>
      {caseNumber && (
        <p className="mt-1 font-mono text-sm text-muted-foreground" data-testid="case-number">{caseNumber}</p>
      )}

      <div className="mt-8 grid gap-8 lg:grid-cols-5">
        {/* Form */}
        <div className="space-y-5 lg:col-span-3">
          <div className="space-y-1.5">
            <Label>{t("case.title")}</Label>
            <Input value={form.title} onChange={(e) => set("title", e.target.value)} placeholder={t("case.titlePlaceholder")} data-testid="case-title" />
          </div>

          <div className="grid gap-5 sm:grid-cols-3">
            <div className="space-y-1.5">
              <Label>{t("case.deadline")}</Label>
              <Input type="date" value={form.deadline} onChange={(e) => set("deadline", e.target.value)} data-testid="case-deadline" />
            </div>
            <div className="space-y-1.5">
              <Label>{t("case.offeredPrice")} <span className="text-[11px] font-normal text-muted-foreground">(USD)</span></Label>
              <Input type="number" min="0" value={form.budget} onChange={(e) => set("budget", e.target.value)} placeholder={t("case.offeredPricePlaceholder")} data-testid="case-offered-price" />
            </div>
            <div className="space-y-1.5">
              <Label>{t("odontogram.numbering")}</Label>
              <div className="flex rounded-md border border-input p-0.5" data-testid="numbering-toggle">
                {["fdi", "universal"].map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => set("toothNumbering", n)}
                    data-testid={`numbering-${n}`}
                    className={`flex-1 rounded px-2 py-1.5 text-xs font-medium transition-colors ${numbering === n ? "bg-brand text-white" : "text-muted-foreground hover:bg-secondary"}`}
                  >
                    {t(`odontogram.${n}`)}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* AI Budget Advisor */}
          <div className="rounded-lg border border-indigo-200 bg-indigo-50/40 p-4" data-testid="budget-advisor">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="flex items-center gap-1.5 text-sm font-semibold text-indigo-900">
                <Sparkles className="h-4 w-4" /> {t("budget.title")}
              </p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 rounded-full border-indigo-300 text-indigo-700 hover:bg-indigo-100"
                onClick={runBudgetAdvisor}
                disabled={budgetAi.loading}
                data-testid="budget-advisor-button"
              >
                {budgetAi.loading ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Sparkles className="mr-1.5 h-3.5 w-3.5" />}
                {budgetAi.data ? t("budget.recalculate") : t("budget.getPrice")}
              </Button>
            </div>
            {!budgetAi.data && !budgetAi.loading && (
              <p className="mt-1 text-xs text-indigo-700/80">{t("budget.hint")}</p>
            )}
            {budgetAi.data && (() => {
              const rec = budgetAi.data.recommended;
              const conf = budgetAi.data.confidence || "Medium";
              const confStyle = conf === "High" ? "bg-emerald-100 text-emerald-700" : conf === "Low" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700";
              const reason = budgetAi.data.reason ? (i18n.language?.split("-")[0] === "es" ? budgetAi.data.reason.es : budgetAi.data.reason.en) : "";
              const price = Number(form.budget) || 0;
              const tooLow = price > 0 && price < rec.min * 0.7;
              const tooHigh = price > 0 && price > rec.max * 1.5;
              return (
                <div className="mt-3 space-y-2" data-testid="budget-advisor-result">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs font-medium uppercase tracking-wide text-indigo-700/80">{t("budget.recommendedPrice")}</span>
                    <span className="font-heading text-lg font-bold text-indigo-900" data-testid="budget-recommended-range">USD {rec.min} – {rec.max}</span>
                    <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${confStyle}`} data-testid="budget-confidence">
                      {t("budget.confidence")}: {t(`budget.confidenceLevels.${conf.toLowerCase()}`, conf)}
                    </span>
                  </div>
                  {reason && <p className="text-xs text-indigo-800/90">{reason}</p>}
                  {(tooLow || tooHigh) && (
                    <div className="flex flex-wrap items-center gap-2 rounded-md border border-amber-300 bg-amber-50 p-2.5" data-testid="budget-price-warning">
                      <AlertCircle className="h-4 w-4 shrink-0 text-amber-600" />
                      <span className="text-xs text-amber-800">{tooLow ? t("budget.tooLow") : t("budget.tooHigh")}</span>
                      <Button type="button" size="sm" variant="outline" className="ml-auto h-7 rounded-full text-xs" onClick={() => set("budget", String(Math.round((rec.min + rec.max) / 2)))} data-testid="budget-use-recommended">
                        {t("budget.useRecommended")}
                      </Button>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>

          {/* Restorations */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-heading text-lg font-semibold tracking-tight">{t("restoration.title")}</h2>
                <p className="text-xs text-muted-foreground">{t("restoration.subtitle")}</p>
              </div>
            </div>
            {restorations.map((r, i) => (
              <RestorationForm
                key={r.localId}
                index={i}
                value={r}
                numbering={numbering}
                onChange={(next) => updateRestoration(i, next)}
                onRemove={() => removeRestoration(i)}
                canRemove={restorations.length > 1}
              />
            ))}
            <Button variant="outline" className="w-full rounded-full border-dashed" onClick={addRestoration} data-testid="add-restoration-button">
              <Plus className="mr-1.5 h-4 w-4" /> {t("restoration.add")}
            </Button>
          </div>

          {/* Confidential patient & case identification */}
          <div className="space-y-4 rounded-lg border border-slate-200 bg-slate-50/60 p-4" data-testid="patient-section">
            <div className="flex items-center gap-1.5 text-sm font-semibold">
              <Lock className="h-4 w-4 text-slate-500" /> {t("case.patientSection")}
              <span className="ml-1 rounded-full bg-slate-200 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-slate-600">{t("case.confidential")}</span>
            </div>
            <p className="-mt-2 text-xs text-muted-foreground">{t("case.patientPrivacyHint")}</p>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label>{t("case.patientName")} <span className="text-[11px] font-normal text-muted-foreground">({t("common.optional")})</span></Label>
                <Input value={form.patientName} onChange={(e) => set("patientName", e.target.value)} placeholder={t("case.patientNamePlaceholder")} data-testid="case-patient-name" />
              </div>
              <div className="space-y-1.5">
                <Label>{t("case.patientId")}</Label>
                <Input value={form.patientId} onChange={(e) => set("patientId", e.target.value)} placeholder={t("common.optional")} data-testid="case-patient-id" />
              </div>
              <div className="space-y-1.5">
                <Label>{t("case.doctorName")}</Label>
                <Input value={form.doctorName} onChange={(e) => set("doctorName", e.target.value)} placeholder={t("case.doctorNamePlaceholder")} data-testid="case-doctor-name" />
              </div>
              <div className="space-y-1.5">
                <Label>{t("case.clinicCaseId")}</Label>
                <Input value={form.clinicCaseId} onChange={(e) => set("clinicCaseId", e.target.value)} placeholder={t("common.optional")} data-testid="case-clinic-case-id" />
              </div>
            </div>
            <div className="rounded-md border border-slate-200 bg-white p-3" data-testid="additional-deliverables">
              <p className="mb-2 text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground">{t("case.additionalDeliverables")}</p>
              <label className="flex cursor-pointer items-center gap-2 text-sm" data-testid="toggle-includeModels">
                <input type="checkbox" className="h-4 w-4 rounded border-slate-300" checked={form.includeModels} onChange={(e) => set("includeModels", e.target.checked)} />
                {t("case.includeModels")}
              </label>
              {form.includeModels && (
                <div className="mt-2 space-y-1.5">
                  <Label>{t("case.modelEngraving")}</Label>
                  <Input value={form.modelEngraving} onChange={(e) => set("modelEngraving", e.target.value)} placeholder={t("case.modelEngravingPlaceholder")} data-testid="case-model-engraving" />
                </div>
              )}
              {isImplantCase && (
                <label className="mt-2 flex cursor-pointer items-center gap-2 text-sm" data-testid="toggle-includeGingiva">
                  <input type="checkbox" className="h-4 w-4 rounded border-slate-300" checked={form.includeGingiva} onChange={(e) => set("includeGingiva", e.target.checked)} />
                  {t("case.includeGingiva")}
                </label>
              )}
            </div>
            <div className="rounded-md border border-slate-200 bg-white p-3">
              <p className="mb-2 text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground">{t("case.shareWithDesigner")}</p>
              <div className="space-y-2">
                {[
                  ["sharePatientName", t("case.sharePatientName")],
                  ["shareDoctorName", t("case.shareDoctorName")],
                  ["shareClinicCaseId", t("case.shareClinicCaseId")],
                ].map(([k, label]) => (
                  <label key={k} className="flex cursor-pointer items-center gap-2 text-sm" data-testid={`toggle-${k}`}>
                    <input type="checkbox" className="h-4 w-4 rounded border-slate-300" checked={form[k]} onChange={(e) => set(k, e.target.checked)} />
                    {label}
                  </label>
                ))}
              </div>
              <p className="mt-2 text-[11px] text-muted-foreground">{t("case.shareHint")}</p>
            </div>
          </div>

          {/* Photos */}
          <div className="space-y-1.5">
            <Label className="flex items-center gap-1.5"><ImageIcon className="h-4 w-4" /> {t("case.photos")}</Label>
            <label className="flex cursor-pointer items-center justify-center gap-2 rounded-lg border border-dashed border-border bg-secondary/30 p-4 text-sm text-muted-foreground hover:border-slate-300">
              <Upload className="h-4 w-4" /> {(photos.length || existingPhotos.length) ? t("cad.addMore") : t("case.uploadPhotos")}
              <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => { addPhotos(e.target.files); e.target.value = ""; }} data-testid="case-photos-input" />
            </label>
            {(existingPhotos.length > 0 || photos.length > 0) && (
              <ul className="space-y-1" data-testid="case-photos-list">
                {existingPhotos.map((url, i) => (
                  <li key={`ep-${i}`} className="flex items-center justify-between gap-2 rounded-md border border-border bg-background px-2.5 py-1.5">
                    <span className="truncate text-xs text-muted-foreground">{t("case.photo")} {i + 1}</span>
                    <button type="button" onClick={() => removeExistingPhoto(i)} data-testid={`case-existing-photo-remove-${i}`} aria-label={t("cad.remove")}>
                      <X className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground" />
                    </button>
                  </li>
                ))}
                {photos.map((f, i) => (
                  <li key={`np-${i}`} className="flex items-center justify-between gap-2 rounded-md border border-border bg-background px-2.5 py-1.5">
                    <span className="truncate text-xs text-muted-foreground">{f.name}</span>
                    <button type="button" onClick={() => removePhoto(i)} data-testid={`case-photo-remove-${i}`} aria-label={t("cad.remove")}>
                      <X className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {totalCadCount === 0 && (
              <p className="flex items-center gap-1 text-xs text-amber-600" data-testid="cad-required-hint">
                <AlertCircle className="h-3.5 w-3.5" /> {t("cad.requiredToPublish")}
              </p>
            )}
          </div>
        </div>

        {/* AI + actions */}
        <div className="space-y-5 lg:col-span-2">
          <Button variant="outline" className="w-full rounded-full border-indigo-300 text-indigo-700 hover:bg-indigo-50" onClick={runAnalysis} disabled={aiState.loading} data-testid="analyze-ai-button">
            <Sparkles className="mr-1.5 h-4 w-4" /> {ai ? t("case.reanalyze") : t("case.analyzeAi")}
          </Button>

          <AiAnalysisPanel ai={ai} loading={aiState.loading} error={aiState.error} errorType={aiState.errorType} />

          {stlUp.status !== "idle" && (
            <div className="rounded-lg border border-border bg-card p-4" data-testid="stl-upload-progress">
              <div className="mb-2 flex items-center justify-between">
                <span className="flex items-center gap-1.5 text-sm font-medium">
                  <FileBox className="h-4 w-4" /> {t("upload.title")}
                </span>
                {stlUp.status === "uploading" && (
                  <span className="flex items-center gap-1 text-sm font-mono text-brand" data-testid="stl-upload-percent">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" /> {stlUp.pct}%
                  </span>
                )}
                {stlUp.status === "processing" && (
                  <span className="flex items-center gap-1 text-sm text-brand" data-testid="stl-upload-processing">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" /> {t("upload.processing")}
                  </span>
                )}
                {stlUp.status === "done" && (
                  <span className="flex items-center gap-1 text-sm text-emerald-600" data-testid="stl-upload-done">
                    <CheckCircle2 className="h-4 w-4" /> {t("upload.done")}
                  </span>
                )}
                {stlUp.status === "error" && (
                  <span className="flex items-center gap-1 text-sm text-red-600" data-testid="stl-upload-error">
                    <AlertCircle className="h-4 w-4" /> {t("upload.error")}
                  </span>
                )}
              </div>
              {(stlUp.status === "uploading" || stlUp.status === "processing" || stlUp.status === "done") && (
                <Progress value={stlUp.status === "processing" ? 100 : stlUp.pct} className="h-2" data-testid="stl-upload-bar" />
              )}
              {stlUp.status === "error" && (
                <Button className="mt-3 w-full rounded-full bg-brand text-white hover:bg-brand/90" onClick={retryStl} disabled={saving} data-testid="stl-upload-retry">
                  <RotateCcw className="mr-1.5 h-4 w-4" /> {t("upload.retry")}
                </Button>
              )}
              {stlUp.status === "uploading" && (
                <p className="mt-2 text-xs text-muted-foreground">{t("upload.largeHint")}</p>
              )}
            </div>
          )}

          <div className="flex flex-col gap-2">
            <Button className="w-full rounded-full bg-brand text-white hover:bg-brand/90" onClick={handlePublishClick} disabled={saving || !!createdId || totalCadCount === 0} data-testid="publish-case-button" title={totalCadCount === 0 ? t("cad.requiredToPublish") : undefined}>
              {t("case.publish")}
            </Button>
            <Button variant="secondary" className="w-full rounded-full" onClick={() => save("Draft")} disabled={saving || !!createdId} data-testid="save-draft-button">
              {editId ? t("case.saveChanges") : t("case.saveDraft")}
            </Button>
          </div>
        </div>
      </div>

      <Dialog open={confirmPublishOpen} onOpenChange={setConfirmPublishOpen}>
        <DialogContent data-testid="publish-confirm-dialog">
          <DialogHeader>
            <DialogTitle>{t("case.aiRecommendationsTitle")}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">{t("case.aiRecommendationsBody")}</p>
          <ul className="max-h-48 space-y-1.5 overflow-y-auto rounded-md border border-amber-200 bg-amber-50 p-3" data-testid="publish-confirm-warnings">
            {aiWarnings.map((w, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-amber-800">
                <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {w}
              </li>
            ))}
          </ul>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" className="rounded-full" onClick={() => setConfirmPublishOpen(false)} data-testid="complete-info-button">
              {t("case.completeInfo")}
            </Button>
            <Button className="rounded-full bg-brand text-white hover:bg-brand/90" onClick={() => { setConfirmPublishOpen(false); save("Published", { acknowledgeWarnings: true }); }} data-testid="publish-anyway-button">
              {t("case.publishAnyway")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
